import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Layout from '../components/Layout';
import GroupRentalInvitePanel from '../components/GroupRentalInvitePanel';
import ScoreboardSettingsPanel from '../components/ScoreboardSettingsPanel';
import ScoreboardPreviewPanel from '../components/ScoreboardPreviewPanel';
import { logError } from '../utils/errorLogger';
import { CUSTOM_RATE, customerRate } from '../utils/billingRates';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Alert, CircularProgress, IconButton, Tooltip,
  Tabs, Tab, Divider, InputAdornment, FormControlLabel, Checkbox, Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { rateOn } from '../utils/rentalPricing';

const COURTS = ['court-1','court-2','court-3','court-4','court-5','court-6'];
const COURT_LABELS = { 'court-1':'Court 1','court-2':'Court 2','court-3':'Court 3','court-4':'Court 4','court-5':'Court 5','court-6':'Court 6' };
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const STATUS_COLORS = {
  pending:   { bg: '#FEF9E7', color: '#B7950B' },
  paid:      { bg: '#E9F7EF', color: '#1E6B3C' },
  cancelled: { bg: '#FDEDEC', color: '#922B21' },
};

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function toMins(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

// Split billing: calculate cost across time blocks for a given time range and set of courts
function calculateRentalCost(startTime, endTime, date, courts, timeBlocks, rates, overrideRateId, customRate) {
  if (!startTime || !endTime || !date || !courts.length) return { total: 0, breakdown: [] };

  const allCourts = courts.length === COURTS.length;
  const startMins = toMins(startTime);
  const endMins = toMins(endTime);
  const hours = (endMins - startMins) / 60;
  const dayOfWeek = new Date(date + 'T12:00:00').getDay();
  const dayAbbr = DAYS[dayOfWeek];

  // Which hourly figure applies. Weekend wins over prime, matching how member
  // bookings bucket hours (splitHours3 treats a weekend as wholly weekend and
  // never also prime) — the two sides of the club should not disagree about
  // what a Saturday evening costs. A blank prime/weekend rate falls back to
  // non-prime, so rates defined before this existed price exactly as before.
  const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
  // A card's scheduled change applies by the date played.
  const hourlyFor = (card, prime) => {
    const rate = rateOn(card, date);
    if (isWeekendDay && Number(rate.weekendRate) > 0) return Number(rate.weekendRate);
    if (!isWeekendDay && prime && Number(rate.primeRate) > 0) return Number(rate.primeRate);
    return Number(rate.hourlyRate) || 0;
  };
  const rateSuffix = (card, prime) => {
    const rate = rateOn(card, date);
    if (isWeekendDay && Number(rate.weekendRate) > 0) return ' · weekend';
    if (!isWeekendDay && prime && Number(rate.primeRate) > 0) return ' · prime';
    return '';
  };
  // Minutes covered by a block flagged prime, whatever rate that block carries.
  const primeMins = new Set();
  for (const tb of timeBlocks) {
    if (!tb.isPrime || !tb.days?.includes(dayAbbr)) continue;
    const bs = Math.max(toMins(tb.startTime), startMins);
    const be = Math.min(toMins(tb.endTime), endMins);
    for (let m = bs; m < be; m++) primeMins.add(m);
  }
  // Split a span into prime / non-prime pieces and price each at its own rate.
  const priceSpan = (rate, label, from, to) => {
    const out = [];
    let segStart = from, segPrime = primeMins.has(from);
    for (let m = from + 1; m <= to; m++) {
      const p = m < to ? primeMins.has(m) : null;
      if (p !== segPrime) {
        const h = (m - segStart) / 60;
        out.push({ label: label + rateSuffix(rate, segPrime), hours: h,
          cost: hourlyFor(rate, segPrime) * h * courts.length });
        segStart = m; segPrime = p;
      }
    }
    return out;
  };

  if (overrideRateId) {
    // A per-customer rate isn't in `rates` — it travels with the booking.
    const rate = overrideRateId === CUSTOM_RATE ? customRate : rates.find(r => r.id === overrideRateId);
    if (rate) {
      let cost = 0;
      let label = rate.rateName;
      if (allCourts && rate.facilityRate) {
        cost = rate.facilityRate;
        label += ' (facility · 1 day)';
      } else {
        const parts = priceSpan(rate, label, startMins, endMins);
        return { total: parts.reduce((s2, b) => s2 + b.cost, 0), breakdown: parts };
      }
      return { total: cost, breakdown: [{ label, hours, cost }] };
    }
  }

  const applicable = timeBlocks.filter(tb =>
    tb.days?.includes(dayAbbr) &&
    tb.rateId &&
    toMins(tb.startTime) < endMins &&
    toMins(tb.endTime) > startMins
  );

  if (!applicable.length) {
    const defaultRate = rates[0];
    if (!defaultRate) return { total: 0, breakdown: [] };
    if (allCourts && defaultRate.facilityRate) {
      return { total: defaultRate.facilityRate,
        breakdown: [{ label: defaultRate.rateName + ' (default)', hours, cost: defaultRate.facilityRate }] };
    }
    const parts = priceSpan(defaultRate, defaultRate.rateName + ' (default)', startMins, endMins);
    return { total: parts.reduce((s2, b) => s2 + b.cost, 0), breakdown: parts };
  }

  const sortedByDurationDesc = [...applicable].sort((a, b) => {
    const aDur = toMins(a.endTime) - toMins(a.startTime);
    const bDur = toMins(b.endTime) - toMins(b.startTime);
    return bDur - aDur;
  });

  const slotMap = new Map();
  for (const block of sortedByDurationDesc) {
    const blockStart = Math.max(toMins(block.startTime), startMins);
    const blockEnd = Math.min(toMins(block.endTime), endMins);
    for (let m = blockStart; m < blockEnd; m++) {
      slotMap.set(m, block.rateId);
    }
  }

  const fallbackRateId = rates[0]?.id;
  for (let m = startMins; m < endMins; m++) {
    if (!slotMap.has(m) && fallbackRateId) slotMap.set(m, fallbackRateId);
  }

  const breakdown = [];
  let segStart = startMins;
  let segRateId = slotMap.get(startMins);
  for (let m = startMins + 1; m <= endMins; m++) {
    const currentRateId = m < endMins ? slotMap.get(m) : null;
    if (currentRateId !== segRateId) {
      if (segRateId) {
        const rate = rates.find(r => r.id === segRateId);
        if (rate) breakdown.push(...priceSpan(rate, rate.rateName, segStart, m));
      }
      segStart = m;
      segRateId = currentRateId;
    }
  }

  const total = breakdown.reduce((sum, b) => sum + b.cost, 0);
  return { total, breakdown };
}

// Find any blockReservations time block that overlaps a proposed reservation.
// Returns the first matching block, or null. Used to gate non-admin bookings.
function findBlockingTimeBlock(startTime, endTime, date, timeBlocks) {
  if (!startTime || !endTime || !date) return null;
  const startMins = toMins(startTime);
  const endMins = toMins(endTime);
  const dayOfWeek = new Date(date + 'T12:00:00').getDay();
  const dayAbbr = DAYS[dayOfWeek];
  return timeBlocks.find(tb =>
    tb.blockReservations === true &&
    tb.days?.includes(dayAbbr) &&
    toMins(tb.startTime) < endMins &&
    toMins(tb.endTime) > startMins
  ) || null;
}

// hourlyRate is the NON-PRIME rate. The key keeps its old name deliberately:
// renaming it would strand the rate docs already in use and the bookings
// priced from them. primeRate and weekendRate are additive — blank means
// "same as non-prime", which is how every existing rate behaves today.
const emptyRate = { rateName: '', hourlyRate: '', primeRate: '', weekendRate: '', dailyRate: '', facilityRate: '',
  changeFrom: '', changeHourly: '', changePrime: '', changeWeekend: '' };
const emptyDiscount = { name: '', discountType: '%', amount: '' };
const emptyBlock = { name: '', days: [], startTime: '07:30', endTime: '22:00', rateId: '', blockReservations: false, isPrime: false };
const emptyBooking = {
  clientName: '', clientEmail: '', clientPhone: '',
  date: '', startTime: '08:00', endTime: '10:00',
  courts: [], notes: '', overrideRateId: '', customerId: '', customRate: null,
};

// Policy guardrails (stored at settings/rentalPolicy). Admins bypass all of these.
// Tier-scoped overrides live on each membershipTier doc (maxHoursPerWeek, maxCourtsPerRequest).
const defaultPolicy = {
  maxBookingDurationHours: 2.5,
  maxAdvanceDays: 14,
  minAdvanceHours: 1,
  cancelWindowHours: 1,
  defaultMaxCourts: 2,
  // Off by default: a late cancel returns the member's hours, which is how it
  // has always worked. Turning this on is a rule the club announces.
  enforceLateCancelHours: false,
};

export default function CourtRentals() {
  const [tab, setTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [rates, setRates] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [policy, setPolicy] = useState(defaultPolicy);

  // Rate dialog
  const [rateDialog, setRateDialog] = useState(false);
  const [editingRateId, setEditingRateId] = useState(null);
  const [rateForm, setRateForm] = useState(emptyRate);

  // Time block dialog
  const [blockDialog, setBlockDialog] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [blockForm, setBlockForm] = useState(emptyBlock);

  // Booking dialog
  const [bookingDialog, setBookingDialog] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [bookingForm, setBookingForm] = useState(emptyBooking);
  const [costPreview, setCostPreview] = useState({ total: 0, breakdown: [] });
  const [customers, setCustomers] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);

  // Discount dialog
  const [discountDialog, setDiscountDialog] = useState(false);
  const [editingDiscountId, setEditingDiscountId] = useState(null);
  const [discountForm, setDiscountForm] = useState(emptyDiscount);

  useEffect(() => {
    const load = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const roles = userDoc.data().roles || [];
          // court_admin is a calendar role only — no admin rights here.
          setIsAdmin(roles.includes('admin'));
        }
      }
      const [rSnap, tbSnap, bSnap, dSnap, policySnap, membershipSettingsSnap] = await Promise.all([
        getDocs(collection(db, 'rentalRates')),
        getDocs(collection(db, 'rentalTimeBlocks')),
        getDocs(collection(db, 'rentalBookings')),
        getDocs(collection(db, 'rentalDiscounts')),
        getDoc(doc(db, 'settings', 'rentalPolicy')),
        getDoc(doc(db, 'settings', 'membershipTiers')), // for cancelWindowHours fallback only
      ]);
      setRates(rSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeBlocks(tbSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setBookings(bSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a,b) => (b.date || '').localeCompare(a.date || '')));
      setDiscounts(dSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      // Customers for the booking dialog picker (admin-only read; ignore denial)
      try {
        const cSnap = await getDocs(collection(db, 'customers'));
        setCustomers(cSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(c => !c.archived)
          .sort((a, b) => (a.organizationName || '').localeCompare(b.organizationName || '')));
      } catch (e) { /* non-admin viewers simply get no picker options */ }
      // Policy: prefer settings/rentalPolicy; on first run, inherit cancelWindowHours from
      // settings/membershipTiers (where it used to live) so behavior doesn't change.
      if (policySnap.exists()) {
        setPolicy({ ...defaultPolicy, ...policySnap.data() });
      } else if (membershipSettingsSnap.exists()) {
        const ms = membershipSettingsSnap.data();
        setPolicy({ ...defaultPolicy, cancelWindowHours: ms.cancelWindowHours ?? defaultPolicy.cancelWindowHours });
      }
      setLoading(false);
    };
    load().catch(e => { logError('CourtRentals', e.message, e.stack); setLoading(false); });
  }, []);

  // Recalculate cost preview when booking form changes
  useEffect(() => {
    if (bookingDialog) {
      const preview = calculateRentalCost(
        bookingForm.startTime, bookingForm.endTime, bookingForm.date,
        bookingForm.courts, timeBlocks, rates, bookingForm.overrideRateId, bookingForm.customRate
      );
      setCostPreview(preview);
    }
  }, [bookingForm.startTime, bookingForm.endTime, bookingForm.date, bookingForm.courts, bookingForm.overrideRateId, bookingForm.customRate, bookingDialog]);

  // ── Rates ──────────────────────────────────────────────────────────────────
  const openNewRate = () => { setEditingRateId(null); setRateForm(emptyRate); setRateDialog(true); };
  const openEditRate = (r) => { setEditingRateId(r.id); setRateForm({ rateName: r.rateName, hourlyRate: String(r.hourlyRate || ''), primeRate: String(r.primeRate || ''), weekendRate: String(r.weekendRate || ''), dailyRate: String(r.dailyRate || ''), facilityRate: String(r.facilityRate || ''),
    changeFrom: r.scheduledChange?.from || '', changeHourly: String(r.scheduledChange?.hourlyRate ?? ''),
    changePrime: String(r.scheduledChange?.primeRate ?? ''), changeWeekend: String(r.scheduledChange?.weekendRate ?? '') }); setRateDialog(true); };

  const saveRate = async () => {
    if (!rateForm.rateName.trim()) { setError('Rate name is required.'); return; }
    setSaving(true); setError('');
    try {
      const data = { rateName: rateForm.rateName.trim(), hourlyRate: parseFloat(rateForm.hourlyRate) || 0, primeRate: parseFloat(rateForm.primeRate) || 0, weekendRate: parseFloat(rateForm.weekendRate) || 0, dailyRate: parseFloat(rateForm.dailyRate) || 0, facilityRate: parseFloat(rateForm.facilityRate) || 0 };
      // A blank figure in the scheduled change leaves that rate as it is.
      const num = (v) => (String(v).trim() === '' || isNaN(parseFloat(v)) ? null : parseFloat(v));
      data.scheduledChange = rateForm.changeFrom
        ? Object.fromEntries(Object.entries({ from: rateForm.changeFrom, hourlyRate: num(rateForm.changeHourly),
          primeRate: num(rateForm.changePrime), weekendRate: num(rateForm.changeWeekend) }).filter(([, v]) => v !== null))
        : null;
      if (editingRateId) {
        await updateDoc(doc(db, 'rentalRates', editingRateId), data);
        setRates(prev => prev.map(r => r.id === editingRateId ? { id: editingRateId, ...data } : r));
      } else {
        const ref = await addDoc(collection(db, 'rentalRates'), { ...data, createdAt: serverTimestamp() });
        setRates(prev => [...prev, { id: ref.id, ...data }]);
      }
      setRateDialog(false); setSuccess('Rate saved.'); setTimeout(() => setSuccess(''), 3000);
    } catch (e) { logError('CourtRentals', e.message, e.stack); setError('Failed to save rate.'); }
    setSaving(false);
  };

  const deleteRate = async (r) => {
    if (!window.confirm(`Delete rate "${r.rateName}"?`)) return;
    try { await deleteDoc(doc(db, 'rentalRates', r.id)); setRates(prev => prev.filter(x => x.id !== r.id)); setSuccess('Rate deleted.'); setTimeout(() => setSuccess(''), 3000); }
    catch (e) { logError('CourtRentals', e.message, e.stack); setError('Failed to delete.'); }
  };

  // ── Time Blocks ────────────────────────────────────────────────────────────
  const openNewBlock = () => { setEditingBlockId(null); setBlockForm(emptyBlock); setBlockDialog(true); };
  const openEditBlock = (b) => { setEditingBlockId(b.id); setBlockForm({ name: b.name, days: b.days || [], startTime: b.startTime, endTime: b.endTime, rateId: b.rateId, blockReservations: !!b.blockReservations, isPrime: !!b.isPrime }); setBlockDialog(true); };

  const saveBlock = async () => {
    if (!blockForm.name.trim() || !blockForm.rateId) { setError('Name and rate are required.'); return; }
    setSaving(true); setError('');
    try {
      const data = { name: blockForm.name.trim(), days: blockForm.days, startTime: blockForm.startTime, endTime: blockForm.endTime, rateId: blockForm.rateId, blockReservations: !!blockForm.blockReservations, isPrime: !!blockForm.isPrime };
      if (editingBlockId) {
        await updateDoc(doc(db, 'rentalTimeBlocks', editingBlockId), data);
        setTimeBlocks(prev => prev.map(b => b.id === editingBlockId ? { id: editingBlockId, ...data } : b));
      } else {
        const ref = await addDoc(collection(db, 'rentalTimeBlocks'), { ...data, createdAt: serverTimestamp() });
        setTimeBlocks(prev => [...prev, { id: ref.id, ...data }]);
      }
      setBlockDialog(false); setSuccess('Time block saved.'); setTimeout(() => setSuccess(''), 3000);
    } catch (e) { logError('CourtRentals', e.message, e.stack); setError('Failed to save time block.'); }
    setSaving(false);
  };

  const deleteBlock = async (b) => {
    if (!window.confirm(`Delete time block "${b.name}"?`)) return;
    try { await deleteDoc(doc(db, 'rentalTimeBlocks', b.id)); setTimeBlocks(prev => prev.filter(x => x.id !== b.id)); setSuccess('Time block deleted.'); setTimeout(() => setSuccess(''), 3000); }
    catch (e) { logError('CourtRentals', e.message, e.stack); setError('Failed to delete.'); }
  };

  // ── Bookings ───────────────────────────────────────────────────────────────
  const openNewBooking = () => { setEditingBookingId(null); setBookingForm(emptyBooking); setBookingDialog(true); };
  const openEditBooking = (b) => {
    setEditingBookingId(b.id);
    setBookingForm({ clientName: b.clientName || '', clientEmail: b.clientEmail || '', clientPhone: b.clientPhone || '', date: b.date || '', startTime: b.startTime || '08:00', endTime: b.endTime || '10:00', courts: b.courts || [], notes: b.notes || '', overrideRateId: b.overrideRateId || '', customerId: b.customerId || '', customRate: b.customRate || null });
    setBookingDialog(true);
  };

  const saveBooking = async (status = 'pending') => {
    if (!bookingForm.clientName.trim() || !bookingForm.date || !bookingForm.courts.length) { setError('Client name, date, and at least one court are required.'); return; }
    // Non-admins are subject to all guardrails. Admins bypass everything below.
    if (!isAdmin) {
      // 1. Time-block restriction: any block with blockReservations=true is off-limits.
      const blocker = findBlockingTimeBlock(bookingForm.startTime, bookingForm.endTime, bookingForm.date, timeBlocks);
      if (blocker) {
        setError(`Reservations are not allowed during the "${blocker.name}" block. Please choose a different time.`);
        return;
      }

      // 2. Policy guardrails (settings/rentalPolicy).
      // Note: tier-scoped maxHoursPerWeek / maxCourtsPerRequest live on tier docs and are
      // only enforced when bookings are made by members through the member-facing flow,
      // not yet wired into this admin booking dialog.
      const startMins = toMins(bookingForm.startTime);
      const endMins = toMins(bookingForm.endTime);
      const durationHours = (endMins - startMins) / 60;

      if (policy.maxBookingDurationHours > 0 && durationHours > policy.maxBookingDurationHours) {
        setError(`Bookings can be at most ${policy.maxBookingDurationHours} hours long.`);
        return;
      }

      if (policy.defaultMaxCourts > 0 && bookingForm.courts.length > policy.defaultMaxCourts) {
        setError(`You can request at most ${policy.defaultMaxCourts} court${policy.defaultMaxCourts === 1 ? '' : 's'} per booking.`);
        return;
      }

      // Date/time advance checks: compute hours between now and booking start.
      const bookingStart = new Date(`${bookingForm.date}T${bookingForm.startTime}:00`);
      const now = new Date();
      const hoursUntilStart = (bookingStart.getTime() - now.getTime()) / (1000 * 60 * 60);

      if (policy.minAdvanceHours > 0 && hoursUntilStart < policy.minAdvanceHours) {
        setError(`Bookings must be made at least ${policy.minAdvanceHours} hour${policy.minAdvanceHours === 1 ? '' : 's'} in advance.`);
        return;
      }

      if (policy.maxAdvanceDays > 0 && hoursUntilStart > policy.maxAdvanceDays * 24) {
        setError(`Bookings can be made at most ${policy.maxAdvanceDays} day${policy.maxAdvanceDays === 1 ? '' : 's'} ahead.`);
        return;
      }
    }
    setSaving(true); setError('');
    try {
      const cost = calculateRentalCost(bookingForm.startTime, bookingForm.endTime, bookingForm.date, bookingForm.courts, timeBlocks, rates, bookingForm.overrideRateId, bookingForm.customRate);
      const data = { ...bookingForm, totalCost: cost.total, costBreakdown: cost.breakdown, status, updatedAt: serverTimestamp() };
      let savedId = editingBookingId;
      if (editingBookingId) {
        await updateDoc(doc(db, 'rentalBookings', editingBookingId), data);
        setBookings(prev => prev.map(b => b.id === editingBookingId ? { id: editingBookingId, ...data } : b));
        setSuccess('Booking updated.');
      } else {
        data.createdAt = serverTimestamp();
        const ref = await addDoc(collection(db, 'rentalBookings'), data);
        savedId = ref.id;
        setBookings(prev => [{ id: ref.id, ...data }, ...prev]);
        setSuccess('Booking created.');
      }
      setBookingDialog(false); setTimeout(() => setSuccess(''), 3000);
      setSaving(false);
      // Returned so checkout can name the booking it is charging for: the
      // server reads the price off that row rather than being told it.
      return savedId;
    } catch (e) { logError('CourtRentals', e.message, e.stack); setError('Failed to save booking.'); }
    setSaving(false);
    return null;
  };

  const handleStripeCheckout = async () => {
    if (!bookingForm.clientEmail.trim()) { setError('Client email is required for payment.'); return; }
    // Saved first, because the booking is now what sets the price: the server
    // reads totalCost off the row instead of taking an amount from here.
    const bookingId = await saveBooking('pending');
    if (!bookingId) return;
    setCheckingOut(true);
    try {
      const createSession = httpsCallable(getFunctions(), 'createRentalCheckoutSession');
      const result = await createSession({ bookingId });
      window.open(result.data.url, '_blank');
    } catch (e) {
      logError('CourtRentals', e.message, e.stack);
      setError(e.message || 'Checkout failed.');
    }
    setCheckingOut(false);
  };

  const updateBookingStatus = async (booking, status) => {
    try {
      await updateDoc(doc(db, 'rentalBookings', booking.id), { status, updatedAt: serverTimestamp() });
      setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status } : b));
      setSuccess(`Booking marked as ${status}.`); setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError('Failed to update status.'); }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm('Delete this booking?')) return;
    try { await deleteDoc(doc(db, 'rentalBookings', b.id)); setBookings(prev => prev.filter(x => x.id !== b.id)); }
    catch (e) { setError('Failed to delete.'); }
  };

  const toggleCourt = (courtId) => {
    setBookingForm(f => ({
      ...f, courts: f.courts.includes(courtId) ? f.courts.filter(c => c !== courtId) : [...f.courts, courtId]
    }));
  };

  // ── Discounts ──────────────────────────────────────────────────────────────
  const openNewDiscount = () => { setEditingDiscountId(null); setDiscountForm(emptyDiscount); setDiscountDialog(true); };
  const openEditDiscount = (d) => { setEditingDiscountId(d.id); setDiscountForm({ name: d.name, discountType: d.discountType || '%', amount: String(d.amount || '') }); setDiscountDialog(true); };

  const saveDiscount = async () => {
    if (!discountForm.name.trim() || !discountForm.amount) { setError('Name and amount are required.'); return; }
    setSaving(true); setError('');
    try {
      const data = { name: discountForm.name.trim(), discountType: discountForm.discountType, amount: parseFloat(discountForm.amount) || 0 };
      if (editingDiscountId) {
        await updateDoc(doc(db, 'rentalDiscounts', editingDiscountId), data);
        setDiscounts(prev => prev.map(d => d.id === editingDiscountId ? { id: editingDiscountId, ...data } : d));
      } else {
        const ref = await addDoc(collection(db, 'rentalDiscounts'), { ...data, createdAt: serverTimestamp() });
        setDiscounts(prev => [...prev, { id: ref.id, ...data }]);
      }
      setDiscountDialog(false); setSuccess('Discount saved.'); setTimeout(() => setSuccess(''), 3000);
    } catch (e) { logError('CourtRentals', e.message, e.stack); setError('Failed to save discount.'); }
    setSaving(false);
  };

  const deleteDiscount = async (d) => {
    if (!window.confirm(`Delete discount "${d.name}"?`)) return;
    try { await deleteDoc(doc(db, 'rentalDiscounts', d.id)); setDiscounts(prev => prev.filter(x => x.id !== d.id)); setSuccess('Discount deleted.'); setTimeout(() => setSuccess(''), 3000); }
    catch (e) { logError('CourtRentals', e.message, e.stack); setError('Failed to delete.'); }
  };

  // ── Policy ─────────────────────────────────────────────────────────────────
  // Saves a partial patch to settings/rentalPolicy. Persists optimistically so
  // the UI reflects the change immediately; reverts on error.
  const savePolicy = async (patch) => {
    const prev = policy;
    const next = { ...policy, ...patch };
    setPolicy(next);
    try {
      await setDoc(doc(db, 'settings', 'rentalPolicy'), next, { merge: true });
      setSuccess('Policy saved.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      logError('CourtRentals', e.message, e.stack);
      setError('Failed to save policy.');
      setPolicy(prev);
    }
  };

  if (loading) return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>;

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SportsVolleyballIcon sx={{ color: '#1E3A5F', fontSize: 32 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>Court Rentals</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>Manage rates, time blocks, bookings, discounts, and group rental invites</Typography>
            </Box>
          </Box>
          {tab === 0 && <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewRate}>New Rate</Button>}
          {tab === 1 && <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewBlock}>New Time Block</Button>}
          {tab === 2 && <Button variant="contained" startIcon={<AddIcon />} onClick={openNewBooking} sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>New Booking</Button>}
          {tab === 3 && <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewDiscount}>New Discount</Button>}
        </Box>

        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
        {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Tab label="Rates" />
          <Tab label="Time Blocks" />
          <Tab label="Bookings" />
          <Tab label="Discounts" />
          <Tab label="Policy" />
          <Tab label="Group Rental Invites" />
          <Tab label="Scoreboards" />
        </Tabs>

        {/* ── Tab 0: Rates ── */}
        {tab === 0 && (
          <Grid container spacing={2}>
            {rates.length === 0 && <Grid size={{ xs: 12 }}><Typography sx={{ color: '#888', textAlign: 'center', py: 4 }}>No rates defined yet. Add your first rate.</Typography></Grid>}
            {rates.map(r => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={r.id}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{r.rateName}</Typography>
                    <Box>
                      <IconButton size="small" onClick={() => openEditRate(r)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteRate(r)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {r.hourlyRate > 0 && <Typography variant="body2" sx={{ color: '#555' }}><strong>${r.hourlyRate.toFixed(2)}</strong> / hour / court · non-prime</Typography>}
                    {r.primeRate > 0 && <Typography variant="body2" sx={{ color: '#555' }}><strong>${r.primeRate.toFixed(2)}</strong> / hour / court · prime</Typography>}
                    {r.weekendRate > 0 && <Typography variant="body2" sx={{ color: '#555' }}><strong>${r.weekendRate.toFixed(2)}</strong> / hour / court · weekend</Typography>}
                    {r.dailyRate > 0  && <Typography variant="body2" sx={{ color: '#555' }}><strong>${r.dailyRate.toFixed(2)}</strong> / day / court</Typography>}
                    {r.facilityRate > 0 && <Typography variant="body2" sx={{ color: '#27AE60' }}><strong>${r.facilityRate.toFixed(2)}</strong> / day (full facility)</Typography>}
                    {r.scheduledChange?.from && (
                      <Typography variant="body2" sx={{ color: '#B9770E', mt: 0.5 }}>
                        From {new Date(`${r.scheduledChange.from}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}:{' '}
                        {[['hourlyRate', 'non-prime'], ['primeRate', 'prime'], ['weekendRate', 'weekend']]
                          .filter(([k]) => r.scheduledChange[k] != null)
                          .map(([k, l]) => `$${Number(r.scheduledChange[k]).toFixed(2)} ${l}`).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Tab 1: Time Blocks ── */}
        {tab === 1 && (
          <Grid container spacing={2}>
            {timeBlocks.length === 0 && <Grid size={{ xs: 12 }}><Typography sx={{ color: '#888', textAlign: 'center', py: 4 }}>No time blocks defined yet.</Typography></Grid>}
            {timeBlocks.map(b => {
              const rate = rates.find(r => r.id === b.rateId);
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={b.id}>
                  <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{b.name}</Typography>
                      <Box>
                        <IconButton size="small" onClick={() => openEditBlock(b)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteBlock(b)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#555', mb: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      {fmtTime(b.startTime)} – {fmtTime(b.endTime)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                      {(b.days || []).map(d => <Chip key={d} label={d} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />)}
                    </Box>
                    {rate && <Chip label={rate.rateName} size="small" color="primary" sx={{ fontSize: '0.72rem' }} />}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* ── Tab 2: Bookings ── */}
        {tab === 2 && (
          <Box>
            {bookings.length === 0 && <Typography sx={{ color: '#888', textAlign: 'center', py: 4 }}>No bookings yet.</Typography>}
            {bookings.map(b => (
              <Paper key={b.id} elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee', mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{b.clientName}</Typography>
                    <Typography variant="body2" sx={{ color: '#888' }}>{b.clientEmail}{b.clientPhone ? ` · ${b.clientPhone}` : ''}</Typography>
                    <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
                      {b.date} · {fmtTime(b.startTime)}–{fmtTime(b.endTime)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {(b.courts || []).map(c => <Chip key={c} label={COURT_LABELS[c]} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />)}
                    </Box>
                    {b.notes && <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>{b.notes}</Typography>}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#27AE60' }}>${(b.totalCost || 0).toFixed(2)}</Typography>
                    <Chip label={b.status || 'pending'} size="small"
                      sx={{ backgroundColor: STATUS_COLORS[b.status]?.bg || STATUS_COLORS.pending.bg, color: STATUS_COLORS[b.status]?.color || STATUS_COLORS.pending.color, fontWeight: 600, mb: 1 }} />
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openEditBooking(b)}>Edit</Button>
                      {b.status !== 'paid' && <Button size="small" color="success" onClick={() => updateBookingStatus(b, 'paid')}>Mark Paid</Button>}
                      {b.status !== 'cancelled' && <Button size="small" color="warning" onClick={() => updateBookingStatus(b, 'cancelled')}>Cancel</Button>}
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteBooking(b)}>Delete</Button>
                    </Box>
                  </Box>
                </Box>
                {b.costBreakdown?.length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Cost Breakdown</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {b.costBreakdown.map((seg, i) => (
                        <Typography key={i} variant="caption" sx={{ color: '#555' }}>
                          {seg.label}: {seg.hours.toFixed(2)}h × ${(seg.cost / seg.hours).toFixed(2)} = ${seg.cost.toFixed(2)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}

        {/* ── Tab 3: Discounts ── */}
        {tab === 3 && (
          <Grid container spacing={2}>
            {discounts.length === 0 && <Grid size={{ xs: 12 }}><Typography sx={{ color: '#888', textAlign: 'center', py: 4 }}>No discounts defined yet.</Typography></Grid>}
            {discounts.map(d => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={d.id}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{d.name}</Typography>
                    <Box>
                      <IconButton size="small" onClick={() => openEditDiscount(d)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteDiscount(d)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={d.discountType === '%' ? `${d.amount}% off` : d.discountType === '$/hr' ? `$${d.amount}/hr off` : `$${d.amount} off`}
                      size="small"
                      sx={{ backgroundColor: '#EAF4FB', color: '#1A5276', fontWeight: 600 }}
                    />
                    <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>
                      {d.discountType === '%' ? 'Percentage discount' : d.discountType === '$/hr' ? 'Per-hour discount' : 'Fixed amount discount'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Tab 4: Policy ── */}
        {tab === 4 && (
          <Box>
            <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee', mb: 3, backgroundColor: '#f9fafb' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
                Reservation Guardrails
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
                These rules are enforced when non-admins create a court reservation. Admins bypass all guardrails.
                Tier-specific limits (max hours per week, max courts per request) are configured on the Membership Tiers page.
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth label="Max Booking Duration (hours)" type="number"
                    value={policy.maxBookingDurationHours}
                    onChange={e => savePolicy({ maxBookingDurationHours: parseFloat(e.target.value) || 0 })}
                    helperText="Longest single reservation. 0 = no limit."
                    inputProps={{ min: 0, step: 0.5 }}
                    sx={{ backgroundColor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth label="Max Advance Booking (days)" type="number"
                    value={policy.maxAdvanceDays}
                    onChange={e => savePolicy({ maxAdvanceDays: parseInt(e.target.value, 10) || 0 })}
                    helperText="How far into the future a reservation can be made. 0 = no limit."
                    inputProps={{ min: 0, step: 1 }}
                    sx={{ backgroundColor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth label="Min Advance Notice (hours)" type="number"
                    value={policy.minAdvanceHours}
                    onChange={e => savePolicy({ minAdvanceHours: parseFloat(e.target.value) || 0 })}
                    helperText="Minimum lead time before the booking starts. 0 = same-day OK."
                    inputProps={{ min: 0, step: 0.5 }}
                    sx={{ backgroundColor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth label="Cancel Window (hours)" type="number"
                    value={policy.cancelWindowHours}
                    onChange={e => savePolicy({ cancelWindowHours: parseFloat(e.target.value) || 0 })}
                    helperText="Cancel at least this far ahead and the charge is released. Inside it, the charge stands."
                    inputProps={{ min: 0, step: 0.5 }}
                    sx={{ backgroundColor: 'white', borderRadius: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', px: 1.5, py: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={policy.enforceLateCancelHours === true}
                          onChange={e => savePolicy({ enforceLateCancelHours: e.target.checked })}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Late cancel also costs the hours</Typography>}
                    />
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: -0.5 }}>
                      Off: a member who cancels inside the window keeps the charge but gets their
                      weekly hours back — so an in-allowance booking costs them nothing. On: the
                      hours are spent too. Renters are unaffected either way; they hold no weekly
                      allowance. Admins always release both.
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth label="Default Max Courts" type="number"
                    value={policy.defaultMaxCourts}
                    onChange={e => savePolicy({ defaultMaxCourts: parseInt(e.target.value, 10) || 0 })}
                    helperText="Used when the requester has no tier override. Members on a tier may have a higher limit."
                    inputProps={{ min: 1, max: 6, step: 1 }}
                    sx={{ backgroundColor: 'white', borderRadius: 1 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* ── Tab 6: Scoreboards ── */}
        {tab === 6 && (
          <Box>
            <ScoreboardSettingsPanel />
            <ScoreboardPreviewPanel />
          </Box>
        )}

        {/* ── Tab 5: Group Rental Invites ── */}
        {/* The rate the invite carries is the rate card on tab 0, which is why
            this belongs here as well as on Content — the two are read together. */}
        {tab === 5 && <GroupRentalInvitePanel />}

        {/* ── Discount Dialog ── */}
        <Dialog open={discountDialog} onClose={() => setDiscountDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>{editingDiscountId ? 'Edit Discount' : 'New Discount'}</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField fullWidth label="Discount Name *" value={discountForm.name} onChange={e => setDiscountForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Member Discount" sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth select label="Discount Type" value={discountForm.discountType} onChange={e => setDiscountForm(f => ({ ...f, discountType: e.target.value }))} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                <MenuItem value="%">Percentage (%)</MenuItem>
                <MenuItem value="$">Fixed Amount ($)</MenuItem>
                <MenuItem value="$/hr">Per Hour ($/hr)</MenuItem>
              </TextField>
              <TextField
                fullWidth label="Amount *" type="number"
                value={discountForm.amount}
                onChange={e => setDiscountForm(f => ({ ...f, amount: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">{discountForm.discountType}</InputAdornment> }}
                inputProps={{ min: 0, step: discountForm.discountType === '%' ? 1 : 0.01, max: discountForm.discountType === '%' ? 100 : undefined }}
                helperText={discountForm.discountType === '%' ? 'Enter a value between 0 and 100' : discountForm.discountType === '$/hr' ? 'Enter the dollar amount to deduct per hour' : 'Enter the dollar amount to deduct'}
                sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDiscountDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveDiscount} disabled={saving} sx={{ backgroundColor: '#1E3A5F' }}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogActions>
        </Dialog>

        {/* ── Rate Dialog ── */}
        <Dialog open={rateDialog} onClose={() => setRateDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>{editingRateId ? 'Edit Rate' : 'New Rate'}</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField fullWidth label="Rate Name *" value={rateForm.rateName} onChange={e => setRateForm(f => ({ ...f, rateName: e.target.value }))} placeholder="e.g. Prime Time" sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="Non-Prime Rate (per hour, per court)" type="number" value={rateForm.hourlyRate} onChange={e => setRateForm(f => ({ ...f, hourlyRate: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="Prime Rate (per hour, per court)" type="number" value={rateForm.primeRate} onChange={e => setRateForm(f => ({ ...f, primeRate: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} helperText="Leave blank to charge the non-prime rate. Prime hours are the blocks flagged prime on this page." sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="Weekend Rate (per hour, per court)" type="number" value={rateForm.weekendRate} onChange={e => setRateForm(f => ({ ...f, weekendRate: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} helperText="Leave blank to charge the non-prime rate. Applies all day Saturday and Sunday." sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="Daily Rate (per court)" type="number" value={rateForm.dailyRate} onChange={e => setRateForm(f => ({ ...f, dailyRate: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <TextField fullWidth label="Facility Rate (all courts / day)" type="number" value={rateForm.facilityRate} onChange={e => setRateForm(f => ({ ...f, facilityRate: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} helperText="Used when all courts are booked for a full day" sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mt: 1 }}>Scheduled change</Typography>
              <Typography variant="caption" sx={{ color: '#666', mt: -1.5 }}>
                New hourly rates for courts played on or after this date. Leave a rate blank to keep it as it is; clear the date to cancel.
              </Typography>
              <TextField fullWidth label="Starting" type="date" value={rateForm.changeFrom} onChange={e => setRateForm(f => ({ ...f, changeFrom: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              {rateForm.changeFrom && [['changeHourly', 'Non-prime'], ['changePrime', 'Prime'], ['changeWeekend', 'Weekend']].map(([k, l]) => (
                <TextField key={k} fullWidth label={`${l} from then`} type="number" value={rateForm[k]} onChange={e => setRateForm(f => ({ ...f, [k]: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setRateDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveRate} disabled={saving} sx={{ backgroundColor: '#1E3A5F' }}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogActions>
        </Dialog>

        {/* ── Time Block Dialog ── */}
        <Dialog open={blockDialog} onClose={() => setBlockDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>{editingBlockId ? 'Edit Time Block' : 'New Time Block'}</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField fullWidth label="Block Name *" value={blockForm.name} onChange={e => setBlockForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Prime Time" sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', mb: 1 }}>Days — click to toggle</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {DAYS.map((d, i) => {
                    const sel = blockForm.days.includes(d);
                    return (
                      <Box key={d} onClick={() => setBlockForm(f => ({ ...f, days: sel ? f.days.filter(x => x !== d) : [...f.days, d] }))}
                        sx={{ px: 1.5, py: 0.6, borderRadius: 1, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, border: `2px solid ${sel ? '#1E3A5F' : '#ddd'}`, backgroundColor: sel ? '#1E3A5F' : 'white', color: sel ? 'white' : '#555', '&:hover': { borderColor: '#1E3A5F' } }}>
                        {DAY_NAMES[i].slice(0,3)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Start Time" type="time" value={blockForm.startTime} onChange={e => setBlockForm(f => ({ ...f, startTime: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
                <TextField fullWidth label="End Time" type="time" value={blockForm.endTime} onChange={e => setBlockForm(f => ({ ...f, endTime: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              </Box>
              <TextField fullWidth select label="Rate *" value={blockForm.rateId} onChange={e => setBlockForm(f => ({ ...f, rateId: e.target.value }))} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                <MenuItem value="">— Select a rate —</MenuItem>
                {rates.map(r => <MenuItem key={r.id} value={r.id}>{r.rateName}</MenuItem>)}
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!blockForm.blockReservations}
                    onChange={e => setBlockForm(f => ({ ...f, blockReservations: e.target.checked }))}
                  />
                }
                label="No private reservations allowed during this block (admins can still book)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!blockForm.isPrime}
                    onChange={e => setBlockForm(f => ({ ...f, isPrime: e.target.checked }))}
                  />
                }
                label="Prime time — member hours in this block count against prime allocation"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setBlockDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveBlock} disabled={saving} sx={{ backgroundColor: '#1E3A5F' }}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogActions>
        </Dialog>

        {/* ── Booking Dialog ── */}
        <Dialog open={bookingDialog} onClose={() => setBookingDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>{editingBookingId ? 'Edit Booking' : 'New Booking'}</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {customers.length > 0 && (
                <TextField select fullWidth label="Customer" value={bookingForm.customerId}
                  onChange={e => {
                    const cust = customers.find(c => c.id === e.target.value);
                    setBookingForm(f => ({
                      ...f,
                      customerId: e.target.value,
                      ...(cust ? {
                        clientName: cust.organizationName || f.clientName,
                        clientEmail: cust.email || f.clientEmail,
                        clientPhone: cust.phone || f.clientPhone,
                        overrideRateId: cust.billingRateId || f.overrideRateId,
                        customRate: customerRate(cust, rates),
                      } : {}),
                    }));
                  }}
                  helperText="Selecting a customer fills contact info and applies their billing rate"
                  sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}>
                  <MenuItem value="">— Walk-in / none —</MenuItem>
                  {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.organizationName}</MenuItem>)}
                </TextField>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Client Name *" value={bookingForm.clientName} onChange={e => setBookingForm(f => ({ ...f, clientName: e.target.value }))} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Email" type="email" value={bookingForm.clientEmail} onChange={e => setBookingForm(f => ({ ...f, clientEmail: e.target.value }))} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
                <TextField fullWidth label="Phone" value={bookingForm.clientPhone} onChange={e => setBookingForm(f => ({ ...f, clientPhone: e.target.value }))} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              </Box>
              <TextField fullWidth label="Date *" type="date" value={bookingForm.date} onChange={e => setBookingForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Start Time" type="time" value={bookingForm.startTime} onChange={e => setBookingForm(f => ({ ...f, startTime: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
                <TextField fullWidth label="End Time" type="time" value={bookingForm.endTime} onChange={e => setBookingForm(f => ({ ...f, endTime: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>Courts *</Typography>
                  <Button size="small" onClick={() => setBookingForm(f => ({ ...f, courts: f.courts.length === COURTS.length ? [] : [...COURTS] }))} sx={{ fontSize: '0.7rem', color: '#1E3A5F', p: 0, minWidth: 0 }}>
                    {bookingForm.courts.length === COURTS.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {COURTS.map(c => {
                    const sel = bookingForm.courts.includes(c);
                    return (
                      <Box key={c} onClick={() => toggleCourt(c)}
                        sx={{ px: 2, py: 0.8, borderRadius: 1, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, border: `2px solid ${sel ? '#1E3A5F' : '#ddd'}`, backgroundColor: sel ? '#1E3A5F' : 'white', color: sel ? 'white' : '#555', transition: 'all 0.15s', '&:hover': { borderColor: '#1E3A5F', backgroundColor: sel ? '#2E6DA4' : '#DEEAF1' } }}>
                        {COURT_LABELS[c]}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <TextField fullWidth select label="Rate Override (optional)" value={bookingForm.overrideRateId} onChange={e => setBookingForm(f => ({ ...f, overrideRateId: e.target.value }))} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} helperText="Leave blank to auto-calculate from time blocks">
                <MenuItem value="">— Auto from time blocks —</MenuItem>
                {rates.map(r => <MenuItem key={r.id} value={r.id}>{r.rateName}</MenuItem>)}
                {/* A customer's own rate isn't in `rates`; without this the select
                    would render blank and MUI would warn about an out-of-range value. */}
                {bookingForm.overrideRateId === CUSTOM_RATE && (
                  <MenuItem value={CUSTOM_RATE}>
                    {bookingForm.customRate?.rateName || 'Custom rate'}
                    {bookingForm.customRate?.hourlyRate != null ? ` — $${bookingForm.customRate.hourlyRate}/hr` : ''}
                    {' (customer)'}
                  </MenuItem>
                )}
              </TextField>

              <TextField fullWidth label="Notes" multiline rows={2} value={bookingForm.notes} onChange={e => setBookingForm(f => ({ ...f, notes: e.target.value }))} sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }} />

              {bookingForm.courts.length > 0 && bookingForm.date && (
                <Paper sx={{ p: 2, backgroundColor: '#f0f8f4', borderRadius: 2, border: '1px solid #d4edda' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E6B3C', mb: 0.5 }}>Estimated Cost</Typography>
                  {costPreview.breakdown.map((seg, i) => (
                    <Typography key={i} variant="body2" sx={{ color: '#555', fontSize: '0.82rem' }}>
                      {seg.label}: {seg.hours.toFixed(2)}h = ${seg.cost.toFixed(2)}
                    </Typography>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#27AE60' }}>Total: ${costPreview.total.toFixed(2)}</Typography>
                </Paper>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setBookingDialog(false)}>Cancel</Button>
            <Button variant="outlined" onClick={() => saveBooking('pending')} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            <Button variant="contained" startIcon={<ReceiptIcon />} onClick={handleStripeCheckout} disabled={saving || checkingOut || !bookingForm.clientEmail}
              sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#1E8449' } }}>
              {checkingOut ? 'Opening...' : 'Charge via Stripe'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}